import React, { FC, Fragment, PropsWithChildren, Ref } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import cx from 'classnames'

const fakeSetOpen = () => {}

interface BaseModalProps {
  open: boolean
  setOpen?: (value: boolean) => void
  /** Only use 's' | 'm' | 'l' | 'full' from now on */
  width?: 's' | 'm' | 'l' | 'full' | 'xs' | 'sm' | 'md' | 'xl' | '2xl' | '3xl'
  bottomSheetOnSmallScreens?: boolean
  noPadding?: boolean
  noMargin?: boolean
  noScroll?: boolean
  className?: string
  dialogPanelRef?: Ref<HTMLDivElement>
}

/** Usage discouraged. Please consider using one of these: @see Dialog @see Modal */
const BaseModal: FC<PropsWithChildren<BaseModalProps>> = ({
  children,
  open,
  setOpen,
  width = 'xl',
  bottomSheetOnSmallScreens,
  noPadding,
  noMargin,
  noScroll,
  dialogPanelRef,
  className,
}) => {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="tw:relative tw:z-40" onClose={setOpen || fakeSetOpen}>
        <Transition.Child
          as={Fragment}
          enter="tw:ease-out tw:duration-300"
          enterFrom="tw:opacity-0"
          enterTo="tw:opacity-100"
          leave="tw:ease-in tw:duration-200"
          leaveFrom="tw:opacity-100"
          leaveTo="tw:opacity-0"
        >
          <div className="tw:fixed tw:inset-0 tw:bg-gray-500/75 tw:transition-opacity" />
        </Transition.Child>
        <div className={cx('tw:fixed tw:inset-0 tw:z-10 tw:w-screen', { 'tw:overflow-y-auto': !noScroll })}>
          <div
            className={cx('tw:flex tw:min-h-full tw:items-end tw:justify-center tw:text-center', {
              'tw:sm:items-center': !noMargin,
            })}
          >
            <Transition.Child
              as={Fragment}
              enter="tw:ease-out tw:duration-300"
              enterFrom="tw:opacity-0 tw:translate-y-4 tw:sm:translate-y-0 tw:sm:scale-95"
              enterTo="tw:opacity-100 tw:translate-y-0 tw:sm:scale-100"
              leave="tw:ease-in tw:duration-200"
              leaveFrom="tw:opacity-100 tw:translate-y-0 tw:sm:scale-100"
              leaveTo="tw:opacity-0 tw:translate-y-4 tw:sm:translate-y-0 tw:sm:scale-95"
            >
              <Dialog.Panel
                ref={dialogPanelRef}
                className={cx(
                  'tw:w-full tw:relative tw:transform tw:bg-white tw:shadow-xl tw:transition-all',
                  'tw:border tw:border-neutrals-300',
                  'tw:text-left',
                  {
                    'tw:rounded-3xl': !bottomSheetOnSmallScreens,
                    'tw:rounded-t-3xl tw:sm:rounded-3xl': bottomSheetOnSmallScreens,
                    'tw:m-4': !bottomSheetOnSmallScreens && !noMargin,
                    'tw:sm:my-8': !noMargin,
                    'tw:overflow-hidden': noScroll,
                    'tw:max-h-screen tw:sm:max-h-[calc(100vh-3.5rem)] tw:overflow-x-hidden tw:overflow-y-auto':
                      !noScroll,
                    'tw:p-6 tw:pt-8': !noPadding,
                    'tw:max-w-[420px]': width == 's' && !bottomSheetOnSmallScreens,
                    'tw:max-w-[580px]': width == 'm' && !bottomSheetOnSmallScreens,
                    'tw:max-w-[830px]': width == 'l' && !bottomSheetOnSmallScreens,
                    'tw:sm:max-w-[420px]': width == 's' && bottomSheetOnSmallScreens,
                    'tw:sm:max-w-[580px]': width == 'm' && bottomSheetOnSmallScreens,
                    'tw:sm:max-w-[830px]': width == 'l' && bottomSheetOnSmallScreens,
                    'tw:sm:max-w-xs': width == 'xs',
                    'tw:sm:max-w-sm': width == 'sm',
                    'tw:sm:max-w-md': width == 'md',
                    'tw:sm:max-w-xl': width == 'xl',
                    'tw:sm:max-w-2xl': width == '2xl',
                    'tw:sm:max-w-3xl': width == '3xl',
                    'tw:sm:max-w-full': width == 'full',
                  },
                  className
                )}
              >
                {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default BaseModal
